/**
 * Session-level mutation helper.
 *
 * Wraps React Query's `setQueryData` + toast feedback + optional audit log
 * growth. Admin actions are fixture-backed right now, so "mutating" means
 * updating the client cache in memory + notifying the operator. When real
 * endpoints land, swap the `mutationFn` for an `apiPost` call and leave the
 * optimistic `updater` in place.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v2
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useToast, type ToastKind } from '../app/components/toast';
import { useAdminSession } from './auth';
import type { AdminAuditEntry, AdminRole } from './types';

export interface MutationOptions<T> {
    /** React Query key to update. If omitted, nothing is cached. */
    queryKey?: readonly unknown[];
    /** Pure updater run against the existing cached value. */
    updater?: (prev: T | undefined) => T;
    /** Toast message on success. */
    success: string;
    /** Optional toast detail line. */
    detail?: string;
    /** Toast shown on failure. Defaults to a generic one. */
    failure?: string;
    /** Optional audit-log entry to append. Only destructive/sensitive actions need this. */
    audit?: Pick<AdminAuditEntry, 'action' | 'target_type' | 'target_id' | 'severity' | 'reason'>;
    /** Toast kind override (defaults to 'success'). */
    kind?: ToastKind;
}

/**
 * Return a `perform()` function that runs the given optimistic cache update,
 * fires the toast, and appends an audit entry if configured.
 *
 * Intentionally NOT a React Query `useMutation` — admin actions in the
 * scaffold are synchronous cache edits, so wrapping useMutation adds noise.
 */
export function useSessionMutation<T>(opts: MutationOptions<T>) {
    const qc = useQueryClient();
    const { toast } = useToast();
    const session = useAdminSession();

    return useCallback(() => {
        try {
            if (opts.queryKey && opts.updater) {
                qc.setQueryData<T>(opts.queryKey as readonly unknown[], (prev) =>
                    opts.updater!(prev)
                );
            }
            if (opts.audit) {
                appendAuditEntry(qc, {
                    ...opts.audit,
                    actor: session.email,
                    actor_role: session.role
                });
            }
            toast(opts.success, { kind: opts.kind ?? 'success', detail: opts.detail });
        } catch (err) {
            toast(opts.failure ?? 'Action failed', {
                kind: 'danger',
                detail: err instanceof Error ? err.message : undefined
            });
        }
    }, [qc, toast, session, opts]);
}

/**
 * Append a new entry to the admin audit log in the cache.
 * Exposed separately so components can record multiple entries in one action.
 */
export function appendAuditEntry(
    qc: ReturnType<typeof useQueryClient>,
    entry: Omit<AdminAuditEntry, 'id' | 'source' | 'ts' | 'ip'> & {
        actor: string;
        actor_role: AdminRole;
    }
) {
    const id = `aa_live_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const now = new Date();
    const ts = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const row: AdminAuditEntry = {
        id,
        source: 'event',
        ts,
        ip: '127.0.0.1',
        ...entry
    } as AdminAuditEntry;
    qc.setQueryData<AdminAuditEntry[]>(['admin', 'admin-audit', 'audit'], (prev) =>
        prev ? [row, ...prev] : [row]
    );
}
